/**
 * Classifying a rejected query's error by kind.
 *
 * Pure and separate from the components that use it so the node-env runner can reach it.
 * This is not incidental: the first version of this check lived inline in AuthLayout, read
 * `error.response.status`, and could therefore NEVER return true — which silently disabled
 * the whole bounded auth-recovery path. No logic test could catch it, because the code was
 * in a file the runner does not load.
 */

/**
 * Is this error an authentication failure (401/403) rather than a server or network fault?
 *
 * Reads `code`, NOT `response.status`. `Http.errorInterceptor` rejects with `HttpError`,
 * which flattens the axios shape to `{ message, code, errors }` — there is no `response` on
 * it (HttpError.ts:14 does `error.response?.status ?? error.code ?? 0`). A response-based
 * check returns undefined for every failure this app produces.
 *
 * Loose equality is deliberate: `code` is `number | string`, because it falls back to
 * axios's string code. `Http.ts:412` compares it the same way for the same reason.
 *
 * Unknown/unreadable → FALSE, i.e. "not an auth failure". That is the honest default: it
 * attributes an unclassifiable failure to us rather than accusing a valid session of having
 * expired.
 *
 * Note this KEEPS classifying correctly during logout suppression, and that is worth
 * knowing because it is not obvious: `errorInterceptor` replaces a failure with
 * `new HttpError({ ...axiosError, code: 'ERR_CANCELED' })` whenever `isLoggingOut` is set,
 * and `refresh()`'s failure paths set it and never clear it. But the spread PRESERVES
 * `response`, and HttpError's constructor prefers `response?.status` over `code`, so a
 * suppressed 401 still arrives here as 401. Pinned by test, because the whole terminal-screen
 * classification rests on it: if HttpError ever stopped preferring the status, every
 * post-logout auth failure would silently be reported to the user as "this is on our end".
 */
export const isAuthFailureError = (error: unknown): boolean => {
  const code = (error as { code?: number | string } | null | undefined)?.code;
  if (code === null || code === undefined) return false;
  return code == 401 || code == 403;
};

/**
 * Is this a client error (4xx) — i.e. a request that will fail identically if repeated?
 *
 * Same `code` read, same reason: HttpError carries no `response`, so a status-based check
 * silently classifies everything as retryable. That is not hypothetical — `shouldRetry`
 * read `.status ?? .response.status` and therefore retried EVERY 4xx three times, directly
 * contradicting its own docblock, and adding ~7s of backoff (1s+2s+4s) before a brands 401
 * could even settle to isError.
 */
export const isClientError = (error: unknown): boolean => {
  const code = Number((error as { code?: number | string } | null | undefined)?.code);
  return Number.isFinite(code) && code >= 400 && code < 500;
};

/**
 * The 4xx statuses that are TRANSIENT — i.e. the same request can succeed if repeated later.
 *
 * 408 Request Timeout, 425 Too Early, 429 Too Many Requests. Backing off and retrying is
 * the specified response to all three (429 exists to ask for exactly that), so lumping them
 * in with "a client error fails identically if repeated" is wrong on its own terms.
 *
 * This exists because fixing isClientError made the retry guard work for the first time, and
 * a guard that never fired was silently providing the correct behaviour here: every 4xx got
 * three retries with 1s/2s/4s backoff, so a rate-limited endpoint recovered on attempt 2.
 * Excluding the whole range would have turned the first 429 into a hard error for every
 * consumer — the fix creating a worse failure than the bug. Widening a guard means asking
 * what the old broken version happened to be getting right.
 */
const TRANSIENT_CLIENT_STATUSES: ReadonlySet<number> = new Set([408, 425, 429]);

export const isTransientClientError = (error: unknown): boolean => {
  const code = Number((error as { code?: number | string } | null | undefined)?.code);
  return TRANSIENT_CLIENT_STATUSES.has(code);
};

/**
 * Is this a 404 — the resource does not exist?
 *
 * Same `code` read as the rest of this module, for the same reason. Split out because
 * "absent" is frequently not a failure at all: an endpoint that 404s a container which is
 * created lazily is reporting "nothing here yet", and the caller's honest translation of
 * that is an empty result, not an error panel.
 *
 * Deliberately narrow — a caller must opt in per endpoint by asking this question. There is
 * no general rule that a 404 is benign, only endpoints where it is.
 */
export const isNotFoundError = (error: unknown): boolean =>
  Number((error as { code?: number | string } | null | undefined)?.code) === 404;

/**
 * The human-readable message off a rejected request, for a log line or an error panel.
 *
 * `HttpError` (HttpError.ts) is a plain class IMPLEMENTING `HttpErrorResponse`, not an
 * `Error` subclass — so `error instanceof Error` is false for every failure this app
 * produces and `String(error)` yields '[object Object]'. Both of those were shipped bugs.
 * Read the field, and fall back to `String()` only for the genuinely foreign shapes
 * (a thrown string, a native TypeError from our own callback).
 *
 * Never returns an empty string: a blank error panel reads as a broken component.
 */
export const getErrorMessage = (
  error: unknown,
  fallback = 'An unexpected error occurred',
): string => {
  // Both reads are guarded, not just the second one. A `message` getter that throws, or a
  // revoked Proxy, throws from the property access itself; and `String()` raises "Cannot convert
  // object to primitive value" for a null-prototype object or a non-callable toString. Callers
  // pass this to a log line or render it inline in JSX, so an uncaught throw here takes down the
  // very report of the failure.
  try {
    const message = (error as { message?: unknown } | null | undefined)?.message;
    if (typeof message === 'string' && message.trim() !== '') return message;
  } catch {
    return fallback;
  }

  if (error === null || error === undefined) return fallback;

  // Only a string or an object is worth stringifying. A thrown number, boolean or symbol would
  // otherwise surface to the reader as "0" or "false" — noise dressed as an explanation.
  if (typeof error === 'string') {
    return error.trim() === '' ? fallback : error;
  }
  if (typeof error !== 'object' && typeof error !== 'function') return fallback;

  try {
    const stringified = String(error);
    return stringified === '[object Object]' || stringified.trim() === '' ? fallback : stringified;
  } catch {
    return fallback;
  }
};

/**
 * Axios error codes that describe a connection problem rather than a verdict from a server.
 *
 * They carry no HTTP status, so the numeric classifiers above cannot see them.
 */
const RETRYABLE_NETWORK_CODES: ReadonlySet<string> = new Set([
  'ERR_NETWORK',
  'ECONNABORTED',
  'ETIMEDOUT',
  'ERR_INTERNET_DISCONNECTED',
]);

/**
 * Is repeating this request within the next few seconds worth anything?
 *
 * Yes for the transient 4xx (408/425/429 — 429 exists to ask for exactly this), for connection
 * failures, and for 5xx: a load-balancer restart or a rate limit clears on its own, so giving up
 * on the first one abandons work that was about to succeed.
 *
 * No for everything else, INCLUDING what cannot be classified. A bare `new Error(...)` — which
 * is what `initializeAgentId` throws for an unrecognised response shape, and what a TypeError in
 * our own code looks like — is deterministic: repeating it for thirty seconds produces thirty
 * seconds of identical failures and then a timeout blamed on something else. A cancellation is
 * likewise not a fault to retry, and is not rare: `Http.errorInterceptor` rejects EVERY request
 * with 'ERR_CANCELED' while `HttpState.isLoggingOut` is set, and `refresh()`'s failure paths set
 * it and never clear it.
 *
 * Note this is deliberately NOT the same policy as `shouldRetry` in reactQueryClient, which
 * governs react-query's automatic retries app-wide and treats anything non-4xx as retryable.
 * Reconciling the two means changing global retry behaviour, which does not belong in this
 * change; keeping this one here at least puts both a single import away from the same
 * classifiers.
 */
export const isWorthRetrying = (error: unknown): boolean => {
  // No error is not a retryable error. Guarded because callers reach this with a null error —
  // a view can outlive the failure that produced it — and `isCancelError` reads `.code` off its
  // argument without a guard of its own.
  if (!error) return false;

  const code = (error as { code?: number | string } | null)?.code;
  if (code === 'ERR_CANCELED') return false;
  if (isTransientClientError(error)) return true;
  if (typeof code === 'string') return RETRYABLE_NETWORK_CODES.has(code);

  const status = Number(code);
  return Number.isFinite(status) && status >= 500 && status < 600;
};
